import { useState } from "react";
import { PageHeader, SectionDivider } from "@/components/primitives";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Just a few words and your name");
      return;
    }
    setSaving(true);
    try {
      await submitContact(form);
      toast.success("Letter posted, thank you");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        kicker="Write to us"
        title="Contact"
        subtitle="Send a letter to the patch"
      />

      <div className="grid sm:grid-cols-3 gap-3 mt-2">
        <a
          href="mailto:Heritagedahliasnz@gmail.com"
          className="h-card p-5 hover:shadow-seal transition-shadow"
          data-testid="contact-email-card"
        >
          <Mail className="text-sage-dark" size={18} />
          <div className="kicker mt-2">Email</div>
          <div className="font-display text-xl text-sage-dark mt-1 break-words">
            Heritagedahliasnz@gmail.com
          </div>
        </a>
        <div className="h-card p-5">
          <MapPin className="text-sage-dark" size={18} />
          <div className="kicker mt-2">Where we are</div>
          <div className="font-display text-xl text-sage-dark mt-1">Mangaroa Valley</div>
          <div className="text-sm text-ink-soft">Upper Hutt, Aotearoa NZ</div>
        </div>
        <div className="h-card p-5">
          <div className="kicker">Hours in the patch</div>
          <div className="font-display text-xl text-sage-dark mt-1">Saturdays, 10–2</div>
          <div className="text-sm text-ink-soft italic">Or by quiet appointment.</div>
        </div>
      </div>

      <div className="mt-10">
        <SectionDivider label="Or leave a note" />
      </div>

      <form onSubmit={submit} className="h-card p-6 mt-4 shadow-card" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Your name</span>
            <input
              className="field"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Margaret"
              data-testid="contact-name"
              required
            />
          </label>
          <label className="block">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Email</span>
            <input
              type="email"
              className="field"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              data-testid="contact-email"
              required
            />
          </label>
        </div>
        <label className="block mt-4">
          <span className="text-sm text-ink-soft mb-1.5 inline-block">Your note</span>
          <textarea
            rows={5}
            className="field resize-y"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="A question, a story, an enquiry about tubers..."
            data-testid="contact-message"
            required
          />
        </label>
        <div className="mt-5 flex justify-end">
          <button type="submit" className="btn-heritage" disabled={saving} data-testid="contact-submit">
            <Send size={16} /> {saving ? "Posting..." : "Post Letter"}
          </button>
        </div>
      </form>
    </div>
  );
}
